import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../../styles/Dashboard.css';
import Navbar from '../navbar';
import Sidebar from './../sidebar/index';
import Typography from '../../components/Typography';
import OverviewCustomCard from '../../components/OverviewCustomCard';
import OverviewList from '../../components/OverviewList';
import InvestmentOptionList from '../../components/InvestmentOptionList';
import MilestoneList from '../../components/MilestoneList';
import TimeList from '../../components/TimeList';
import CustomModal from '../../components/CustomModal';
import CustomRange from '../../components/CustomRange';
import InvestmentGrowthChart from '../../components/dashboard/InvestmentGrowthChart';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [activeInvestment, setActiveInvestment] = useState(null);
  const [activeOption, setInvestmentOption] = useState('keep_roi');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('success');
  const [modalMessage, setModalMessage] = useState('');
  const [title, setTitle] = useState('');
  const [viewMode, setViewMode] = useState('cumulative'); // 'cumulative' or 'monthly'
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Format large numbers (e.g., 560000 -> $560K)
  const formatNumber = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(3)}M`;
    if (value >= 1000) {
      const thousands = value / 1000;
      return thousands % 1 === 0 ? `$${thousands}K` : `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);

        // Select the most recent active investment (status: completed)
        const investments = response.data.investments || [];
        const activeInvestments = investments.filter(
          (inv) => inv.status === 'completed'
        );
        if (activeInvestments.length > 0) {
          setActiveInvestment(activeInvestments[0]); // Set initial active investment
        }
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  // Update activeInvestment when activeIndex changes
  useEffect(() => {
    if (user?.investments) {
      const activeInvestments = user.investments.filter(
        (inv) => inv.status === 'completed'
      );
      // if (activeInvestments.length > 0) {
      //   setActiveInvestment(activeInvestments[activeIndex] || activeInvestments[0]);
      // }
    }
  }, [activeIndex, user]);

  const calculateOverviewStats = () => {
    const investments = user?.investments?.filter(
      (inv) => inv.status === 'completed'
    ) || [];
    const totalActiveInvestmentsL = investments.length || 0;
    const totalInvested = investments.reduce((sum, inv) => sum + inv.initialInvestment, 0);
    const totalReturn = investments.reduce((sum, inv) => sum + inv.initialInvestment + inv.totalROI, 0);
    const totalROI = investments.reduce((sum, inv) => sum + inv.totalROI, 0);
    const totalCurrentROI = investments.reduce((sum, inv) => {
      const now = new Date();
      const startDate = new Date(inv.startDate);
      const monthsElapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24 * 30));
      const currentMonthIndex = Math.min(monthsElapsed, inv.monthlyBreakdown.length - 1);
      return sum + (inv.monthlyBreakdown[currentMonthIndex]?.accumulatedReturn || 0);
    }, 0);
    const averageROI = investments.length > 0
      ? (investments.reduce((sum, inv) => sum + inv.annualROI, 0) / investments.length).toFixed(1)
      : 0;

    return { totalActiveInvestmentsL, totalInvested, totalCurrentROI, totalReturn, averageROI };
  };

  const { totalActiveInvestmentsL, totalInvested, totalCurrentROI, totalReturn, averageROI } = calculateOverviewStats();

  const handleInvestmentOption = (option) => {
    setInvestmentOption(option);
  };

  const handleReInvest = () => {
    navigate('/start-investing');
  };

  // Calculate current ROI and remaining return for the selected investment
  const calculateCurrentROI = (investment) => {
    if (!investment || !investment.monthlyBreakdown) return { currentROI: 0, remainingReturn: 0, currentMonth: 0 };

    const now = new Date();
    const startDate = new Date(investment.startDate);
    const monthsElapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24 * 30)); // Approximate months

    const currentMonthIndex = Math.min(monthsElapsed, investment.monthlyBreakdown.length - 1);
    const currentMonth = currentMonthIndex + 1; // 1-based month
    const currentROI = investment.monthlyBreakdown[currentMonthIndex]?.accumulatedReturn || 0;
    const remainingReturn = investment.totalROI - currentROI;

    return { currentROI, remainingReturn, currentMonth };
  };

  // Calculate time remaining
  const calculateTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  const [timeRemaining, setTimeRemaining] = useState(
    activeInvestment
      ? calculateTimeRemaining(activeInvestment.endDate)
      : { days: 0, hours: 0, minutes: 0, seconds: 0 }
  );

  useEffect(() => {
    if (!activeInvestment) {
      setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(activeInvestment.endDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [activeInvestment]);

  const { currentROI, remainingReturn, currentMonth } = activeInvestment
    ? calculateCurrentROI(activeInvestment)
    : { currentROI: 0, remainingReturn: 0, currentMonth: 0 };

  // Prepare chart data for the selected active investment
  const chartData = activeInvestment
    ? activeInvestment.monthlyBreakdown.map((entry, index) => {
        const currentDate = new Date(activeInvestment.startDate);
        currentDate.setMonth(currentDate.getMonth() + index);
        const monthLabel = currentDate.toLocaleString('default', { month: 'short' }) + ' \'' + currentDate.getFullYear().toString().slice(-2);
        return {
          month: monthLabel,
          cumulative: entry.accumulatedReturn,
          monthly: index === 0 ? entry.accumulatedReturn : entry.accumulatedReturn - activeInvestment.monthlyBreakdown[index - 1].accumulatedReturn,
          initialInvestment: activeInvestment.initialInvestment,
        };
      })
    : [];

  // Prepare milestones for CustomRange based on the selected investment
  const milestoneInterval = activeInvestment ? activeInvestment.term === 12 ? 2 : Math.floor(activeInvestment.term / 6) : 1;
  const milestones = activeInvestment
    ? [
        { month: 0, accumulatedReturn: 0 }, // Starting point
        ...activeInvestment.monthlyBreakdown.filter(entry => entry.month % milestoneInterval === 0),
        activeInvestment.monthlyBreakdown[activeInvestment.monthlyBreakdown.length - 1], // Ensure last month is included
      ].slice(0, activeInvestment.term === 12 ? 13 : 7).map((entry) => ({
        label: entry.month === 0 ? 'Start' : `${entry.month} mo`,
        subLabel: formatNumber(entry.accumulatedReturn),
        position: (entry.month / activeInvestment.term) * 100,
      }))
    : [];

  // Calculate time-based progress for the selected investment
  const calculateTimeProgress = () => {
    if (!activeInvestment || !activeInvestment.startDate || !activeInvestment.term) return 0;

    const now = new Date();
    const startDate = new Date(activeInvestment.startDate);
    const elapsedMs = now - startDate;
    const elapsedSeconds = elapsedMs / 1000;

    // Convert term (in months) to seconds (assuming 30 days per month)
    const totalSeconds = activeInvestment.term * 30 * 24 * 60 * 60; // months * days * hours * minutes * seconds
    const progress = (elapsedSeconds / totalSeconds) * 100;

    // Cap progress at 100% and ensure it doesn't go below 0
    return Math.min(Math.max(progress, 0), 100);
  };

  const calculatePayoutProgress = (investment) => {
    if (!investment) return 0;
    const totalPaid = investment.payouts.reduce((sum, payout) => sum + payout.amount, 0);
    const totalROI = investment.totalROI;
    return totalROI > 0 ? Math.min((totalPaid / totalROI) * 100, 100) : 0;
  };

  const progress = calculatePayoutProgress(activeInvestment);

  return (
    <div>
      {showModal && (
        <CustomModal
          type={modalType}
          title={title}
          message={modalMessage}
          colseText='GET APPROVED'
          onConfirm={handleReInvest}
          onClose={() => setShowModal(false)}
        />
      )}
      <Navbar user={user} isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        closeSidebar={closeSidebar} />
      <div className="flex">
        <Sidebar isOpen={isSidebarOpen} closeSidebar={closeSidebar} />
        <div className={`w-full h-full min-h-screen lg:p-5 bg-[#121212] border-[#353535] border-l`}>
          <div className='grid lg:grid-cols-7 md:grid-cols-2 grid-cols-1 gap-3'>
            <div className='lg:col-span-2 md:col-span-1 col-span-1 rounded-[4px] bg-black px-5 pt-5 pb-2'>
              <Typography text={'overview'} transform={'uppercase'} color={'#FFF'} size={'16px'} family={'Supply'} />
              <div className='mt-6 flex justify-between self-stretch gap-[10px]'>
                <OverviewCustomCard
                  icon={<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <g clipPath="url(#clip0_650_1624)">
                      <path opacity="0.2" d="M14 16.625C14.9283 16.625 15.8185 16.2563 16.4749 15.5999C17.1313 14.9435 17.5 14.0533 17.5 13.125H24.5V7.875C24.5 7.41087 24.3156 6.96575 23.9874 6.63756C23.6592 6.30937 23.2141 6.125 22.75 6.125H5.25C4.78587 6.125 4.34075 6.30937 4.01256 6.63756C3.68437 6.96575 3.5 7.41087 3.5 7.875V13.125H10.5C10.5 14.0533 10.8687 14.9435 11.5251 15.5999C12.1815 16.2563 13.0717 16.625 14 16.625Z" fill="white" />
                      <path d="M22.75 6.125H5.25C4.2835 6.125 3.5 6.9085 3.5 7.875V20.125C3.5 21.0915 4.2835 21.875 5.25 21.875H22.75C23.7165 21.875 24.5 21.0915 24.5 20.125V7.875C24.5 6.9085 23.7165 6.125 22.75 6.125Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3.5 13.125H10.5C10.5 14.0533 10.8687 14.9435 11.5251 15.5999C12.1815 16.2563 13.0717 16.625 14 16.625C14.9283 16.625 15.8185 16.2563 16.4749 15.5999C17.1313 14.9435 17.5 14.0533 17.5 13.125H24.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3.5 9.625H24.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_650_1624">
                        <rect width="28" height="28" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>}
                  title={'Invested'}
                  value={totalInvested ? formatNumber(totalInvested) : '$0'}
                />
                <OverviewCustomCard
                  icon={<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <g clipPath="url(#clip0_650_1607)">
                      <path opacity="0.2" d="M19.2501 10.5787V13.5625C19.2501 15.5312 16.052 17.1555 11.8706 17.4519C13.392 18.0283 15.3564 18.375 17.5001 18.375C22.3323 18.375 26.2501 16.6119 26.2501 14.4375C26.2501 12.5322 23.2445 10.944 19.2501 10.5787Z" fill="white" />
                      <path opacity="0.2" d="M10.5 13.125C15.3325 13.125 19.25 11.3621 19.25 9.1875C19.25 7.01288 15.3325 5.25 10.5 5.25C5.66751 5.25 1.75 7.01288 1.75 9.1875C1.75 11.3621 5.66751 13.125 10.5 13.125Z" fill="white" />
                      <path d="M10.5 13.125C15.3325 13.125 19.25 11.3621 19.25 9.1875C19.25 7.01288 15.3325 5.25 10.5 5.25C5.66751 5.25 1.75 7.01288 1.75 9.1875C1.75 11.3621 5.66751 13.125 10.5 13.125Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M1.75 9.1875V13.5625C1.75 15.7369 5.66781 17.5 10.5 17.5C15.3322 17.5 19.25 15.7369 19.25 13.5625V9.1875" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M7 12.7969V17.1719" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M19.2501 10.5787C23.2445 10.944 26.2501 12.5322 26.2501 14.4375C26.2501 16.6119 22.3323 18.375 17.5001 18.375C15.3564 18.375 13.392 18.0283 11.8706 17.4519" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M8.75 17.4212V18.8125C8.75 20.9869 12.6678 22.75 17.5 22.75C22.3322 22.75 26.25 20.9869 26.25 18.8125V14.4375" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M21 18.0469V22.4219" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 12.7969V22.4219" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_650_1607">
                        <rect width="28" height="28" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>}
                  title={'Current'}
                  value={totalCurrentROI ? formatNumber(totalCurrentROI) : '$0'}
                />
                <OverviewCustomCard
                  icon={<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <g clipPath="url(#clip0_650_1618)">
                      <path opacity="0.2" d="M3.5 5.25H22.75C23.2141 5.25 23.6592 5.43437 23.9874 5.76256C24.3156 6.09075 24.5 6.53587 24.5 7V22.75H3.5V5.25Z" fill="white" />
                      <path d="M24.5 22.75H3.5V5.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M21.875 7.875L14 15.75L10.5 12.25L3.5 19.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M21.875 12.25V7.875H17.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_650_1618">
                        <rect width="28" height="28" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>}
                  title={'Total Return'}
                  value={totalReturn ? formatNumber(totalReturn) : '$0'}
                />
              </div>
              <div className='mt-4'>
                <OverviewList title={'Investments'} value={totalActiveInvestmentsL || '0'} borderTop={true} borderLeft={true} />
                <OverviewList title={'milestones'} value={activeInvestment?.monthlyBreakdown?.length.toString() || '0'} borderTop={true} borderLeft={true} />
              </div>
            </div>
            <div className='lg:col-span-5 md:col-span-1 col-span-1 lg:block hidden rounded-[4px] bg-black px-5 pt-5 pb-2'>
              <Typography text={'ACTIVE INVESTMENTS'} transform={'uppercase'} color={'#FFF'} size={'16px'} family={'Supply'} />
              <div className="relative overflow-x-auto mt-3">
                <div className="relative h-52 scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto">
                  <table className="w-full text-sm text-left rtl:text-right">
                    <thead className="text-xs text-white opacity-60">
                      <tr className="border-b border-[#151515]">
                        {['Amount invested', 'Start Date', 'Period', 'Guaranteed ROI', 'Current amount'].map((heading, index) => (
                          <th key={index} scope="col" className="lg:px-6 px-1 py-3">
                            {heading && <Typography text={heading} size={'14px'} weight={400} />}
                          </th>
                        ))}
                        <th scope="col" className="lg:px-6 px-1 py-3">
                          <Typography text='' size={'14px'} weight={400} />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="overflow-y-auto">
                      {(user?.investments || []).filter(inv => inv.status === 'completed').map((investment, index) => {
                        const startDate = new Date(investment.startDate);
                        const currentROI = calculateCurrentROI(investment).currentROI;
                        return (
                          <tr
                            key={index}
                            onClick={() => { setActiveInvestment(investment); setActiveIndex(index)}}
                            className={`border-b h-12 border-[#151515] text-white ${index === activeIndex ? 'bg-[#DF0F11]' : ''}`}
                          >
                            <td className="lg:px-6 px-1 py-2">
                              <Typography text={formatNumber(investment.initialInvestment)} size={'16px'} lineheight={'170%'} weight={400} />
                            </td>
                            <td className="lg:px-6 px-1 py-2">
                              <Typography text={startDate.toLocaleDateString()} size={'16px'} lineheight={'170%'} weight={400} />
                            </td>
                            <td className="lg:px-6 px-1 py-2">
                              <Typography text={`${investment.term} months`} size={'16px'} lineheight={'170%'} weight={400} />
                            </td>
                            <td className="lg:px-6 px-1 py-2">
                              <Typography text={`${investment.annualROI}%`} size={'16px'} lineheight={'170%'} weight={400} />
                            </td>
                            <td className="lg:px-6 px-1 py-2">
                              <Typography text={formatNumber(currentROI)} size={'16px'} lineheight={'170%'} weight={400} />
                            </td>
                            <td className="lg:px-6 px-1 py-2 text-center">
                              <button
                                onClick={() => navigate('/investments')}
                                className={`inline-flex h-8 px-[14px] py-[10px] items-center justify-center gap-[10px] flex-shrink-0 rounded ${index === activeIndex ? 'bg-[#1B1B1B80]' : 'bg-[#1B1B1B]'}`}
                              >
                                <Typography text={'View More'} size={'12px'} weight={400} lineheight={'170%'} color={'#FFF'} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-span-1 lg:hidden block bg-black px-5 pt-5 pb-2">
              <Typography text={'reinvestment options'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} family={'Supply'} />
              <InvestmentOptionList title={'reinvest with keeping roi'} onClick={() => handleInvestmentOption('keep_roi')} active={activeOption === 'keep_roi'} className={'mt-5'} />
              <InvestmentOptionList title={'reinvest with pocketing roi'} onClick={() => handleInvestmentOption('pocket_roi')} active={activeOption === 'pocket_roi'} className={'mt-2'} />
              <InvestmentOptionList title={'reinvest in another investment'} onClick={() => handleInvestmentOption('another_investment')} active={activeOption === 'another_investment'} className={'mt-2'} />
              <div
                onClick={() => {
                  setTitle('LET’S RE-INVEST!');
                  setModalType('question');
                  setModalMessage('Ready to reinvest with Ash Capital? Let’s get this approved by the Ash Capital team!');
                  setShowModal(true);
                }}
                className='cursor-pointer rounded-[1px] mt-[7px] bg-black h-8 text-white border-[1px] border-[#FFF] flex justify-center items-center px-[10px] py-[14px] gap-[10px] flex-shrink-0'
              >
                <Typography text={'Get Approved'} size={'16px'} />
              </div>
            </div>
          </div>
          <div className='w-full lg:hidden block mt-[14px] rounded-[4px] bg-black px-5 pt-5 pb-2'>
            <Typography text={'ACTIVE INVESTMENTS'} transform={'uppercase'} color={'#FFF'} size={'16px'} family={'Supply'} />
            <div className="relative overflow-x-auto mt-3">
              <div className="relative h-52 scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto">
                <table className="w-full text-sm text-left rtl:text-right">
                  <thead className="text-xs text-white opacity-60">
                    <tr className="border-b border-[#151515]">
                      {['Amount invested', 'Start Date', 'Period', 'Guaranteed ROI', 'Current amount'].map((heading, index) => (
                        <th key={index} scope="col" className="lg:px-6 px-1 min-w-[116px] py-3">
                          {heading && <Typography text={heading} size={'14px'} weight={400} />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {(user?.investments || []).filter(inv => inv.status === 'completed').map((investment, index) => {
                      const startDate = new Date(investment.startDate);
                      const currentROI = calculateCurrentROI(investment).currentROI;
                      return (
                        <tr
                          key={index}
                          onClick={() => { setActiveInvestment(investment); setActiveIndex(index)}}
                          className={`border-b h-12 border-[#151515] text-white ${index === activeIndex ? 'bg-[#DF0F11]' : ''}`}
                        >
                          <td className="lg:px-6 px-1 min-w-[116px] py-2">
                            <Typography text={formatNumber(investment.initialInvestment)} size={'16px'} lineheight={'170%'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px] py-2">
                            <Typography text={startDate.toLocaleDateString()} size={'16px'} lineheight={'170%'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px] py-2">
                            <Typography text={`${investment.term} months`} size={'16px'} lineheight={'170%'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px] py-2">
                            <Typography text={`${investment.annualROI}%`} size={'16px'} lineheight={'170%'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px] py-2">
                            <Typography text={formatNumber(currentROI)} size={'16px'} lineheight={'170%'} weight={400} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className='w-full p-5 rounded-[4px] mt-3 grid grid-cols-3 bg-black'>
            <div className="lg:col-span-2 col-span-3">
              <div className='flex max-[360px]:flex-col justify-between items-start pr-[20px]'>
                <Typography text={'investment growth'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} family={'Supply'} />
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => setViewMode('cumulative')}
                    className={`text-white w-[100px] h-[28px] flex justify-center items-center text-[12px] cursor-pointer rounded-[4px] ${viewMode === 'cumulative' ? 'bg-[#DF0F11]' : 'bg-transparent border border-1 border-white hover:bg-[#DF0F11] hover:border-[#DF0F11] duration-300'}`}
                    style={{ fontFamily: 'Aeonik' }}
                  >
                    Cumulative
                  </button>
                  <button
                    onClick={() => setViewMode('monthly')}
                    className={`text-white w-[100px] h-[28px] flex justify-center items-center text-[12px] cursor-pointer rounded-[4px] ${viewMode === 'monthly' ? 'bg-[#DF0F11]' : 'bg-transparent border border-1 border-white hover:bg-[#DF0F11] hover:border-[#DF0F11] duration-300'}`}
                    style={{ fontFamily: 'Aeonik' }}
                  >
                    Monthly
                  </button>
                </div>
              </div>
              <div className='overflow-x-auto scrollbar-thumb-gray-400'>
                <InvestmentGrowthChart averageROI={activeInvestment?.annualROI || 0} data={chartData} viewMode={viewMode} />
              </div>
            </div>
            <div className="col-span-1 lg:block hidden border-l border-[#2C2C2C] pl-6">
              <Typography text={'reinvestment options'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} family={'Supply'} />
              <InvestmentOptionList title={'reinvest with keeping roi'} onClick={() => handleInvestmentOption('keep_roi')} active={activeOption === 'keep_roi'} className={'mt-5'} />
              <InvestmentOptionList title={'reinvest with pocketing roi'} onClick={() => handleInvestmentOption('pocket_roi')} active={activeOption === 'pocket_roi'} className={'mt-2'} />
              <InvestmentOptionList title={'reinvest in another investment'} onClick={() => handleInvestmentOption('another_investment')} active={activeOption === 'another_investment'} className={'mt-2'} />
              <div
                onClick={() => {
                  setTitle('LET’S RE-INVEST!');
                  setModalType('question');
                  setModalMessage('Ready to reinvest with Ash Capital? Let’s get this approved by the Ash Capital team!');
                  setShowModal(true);
                }}
                className='cursor-pointer rounded-[1px] mt-[7px] bg-black h-8 text-white border-[1px] border-[#FFF] flex justify-center items-center px-[10px] py-[14px] gap-[10px] flex-shrink-0'
              >
                <Typography text={'Get Approved'} size={'16px'} />
              </div>
            </div>
          </div>
          <div className='w-full p-6 rounded-[4px] mt-3 bg-black'>
            <div className='grid grid-cols-11 lg:gap-4 gap-[30px]'>
              <div className="lg:col-span-7 col-span-11 block">
                <Typography text={'milestones'} size={'16px'} family={'Supply'} weight={400} transform={'uppercase'} color={'#FFF'} />
                <MilestoneList className={'mt-[25px]'} title={'Track Your Investment'} content={'Here, you can track your investment progress and upcoming payouts.'} />
                <CustomRange
                  className={''}
                  milestones={milestones}
                  progress={progress}
                />
              </div>
              <div className="lg:col-span-2 md:col-span-6 col-span-11">
                <Typography lineheight={'170%'} text={'Your ROI'} size={'16px'} family={'Supply'} weight={400} color={'#FFF'} />
                <Typography
                  className="mt-[17px]"
                  lineheight={'150%'}
                  text={'Track the return on your investment, along with a clear timeline showing when to expect your next payout.'}
                  size={'14px'}
                  weight={400}
                  opacity={0.6}
                  color={'#FFF'}
                />
                <div className="h-[1px] bg-white opacity-20 mt-[17px] w-full" />
                <div className="mt-[15px] grid grid-cols-8">
                  <div className="col-span-5">
                    <Typography lineheight={'170%'} text={'Current ROI'} size={'12px'} weight={400} color={'#DF0F11'} />
                    <Typography
                      lineheight={'170%'}
                      text={formatNumber(currentROI)}
                      size={'20px'}
                      weight={500}
                      family={'Supply'}
                      color={'#FFF'}
                      className="mt-[12px]"
                    />
                  </div>
                  <div className="col-span-3">
                    <Typography lineheight={'170%'} text={'Target ROI'} size={'12px'} weight={400} color={'#DF0F11'} />
                    <Typography
                      lineheight={'170%'}
                      text={activeInvestment ? formatNumber(activeInvestment.totalROI) : '$0'}
                      size={'20px'}
                      weight={500}
                      family={'Supply'}
                      color={'#FFF'}
                      className="mt-[12px]"
                    />
                  </div>
                </div>
              </div>
              <div className="lg:col-span-2 md:col-span-5 col-span-11">
                <div className='flex items-center justify-between'>
                  <Typography lineheight={'170%'} text={'Time Remaining'} size={'16px'} family={'Supply'} weight={400} color={'#FFF'} />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <mask
                      id="mask0_1797_8621"
                      style={{ maskType: 'alpha' }}
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="14"
                      height="14"
                    >
                      <rect
                        width="14"
                        height="14"
                        transform="matrix(-4.37114e-08 -1 -1 4.37114e-08 14 14)"
                        fill="#D9D9D9"
                      />
                    </mask>
                    <g mask="url(#mask0_1797_8621)">
                      <path
                        d="M1.36815 9.32445L2.19605 10.1523L6.9995 5.34889L11.803 10.1523L12.6309 9.32445L6.9995 3.69309L1.36815 9.32445Z"
                        fill="white"
                      />
                    </g>
                  </svg>
                </div>
                <div className='flex items-center justify-between max-[360px]:self-stretch md:mx-0 mx-5 mt-5 gap-[18px]'>
                  <TimeList title={timeRemaining.days.toString()} content={'Days'} />
                  <TimeList title={timeRemaining.hours.toString()} content={'Hours'} />
                  <TimeList title={timeRemaining.minutes.toString()} content={'Minutes'} />
                  <TimeList title={timeRemaining.seconds.toString()} content={'Seconds'} />
                </div>
                <button
                  onClick={() => navigate('/support')}
                  className='w-full border-[2px] h-[28px] bg-white border-[#FFF] rounded-sm px-[10px] py-[18px] flex items-center justify-center gap-[10px]'
                >
                  <Typography text={'Support Chat'} size={'12px'} lineheight={'170%'} weight={400} color={'#000'} />
                </button>
              </div>
            </div>
            <div className='mt-[35px] h-[1px] opacity-10 bg-white w-full' />
            {/* {activeInvestment?.monthlyBreakdown?.map((milestone, index) => {
              const now = new Date();
              const startDate = new Date(activeInvestment.startDate);
              const monthsElapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24 * 30));
              if (milestone.month <= monthsElapsed) {
                const nextPayout = activeInvestment.monthlyROI;
                return (
                  <React.Fragment key={index}>
                    <MilestoneList
                      className={'py-[18px]'}
                      title={`Milestone ${milestone.month} Completed`}
                      content={`Milestone ${milestone.month} complete — you’ve received ${formatNumber(milestone.accumulatedReturn)}. Your next milestone will return ${formatNumber(nextPayout)}.`}
                    />
                    <div className='h-[1px] opacity-10 bg-white w-full' />
                  </React.Fragment>
                );
              }
              return null;
            })} */}
            {activeInvestment?.payouts?.map((payout, index) => (
              <React.Fragment key={index}>
                <MilestoneList
                  className={'py-[18px]'}
                  title={`Milestone ${index + 1} Completed`}
                  content={`Payout ${index + 1} — You’ve received ${formatNumber(payout.amount)} on ${payout.date ? new Date(payout.date).toLocaleDateString() : `Payout ${index + 1}`}.`}
                />
                <div className='h-[1px] opacity-10 bg-white w-full' />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;